import { useAuth } from '@teko/auth-react';
import {
  Body,
  Caption,
  Col,
  Container,
  CustomLink,
  Divider,
  Row,
  Shape,
  ThemeProps,
  styled,
} from '@teko/ds-desktop';
import {
  ChecklistOutlined,
  DownOutlined,
  LocationOutlined,
  OrderOutlined,
  PromoOutlined,
} from '@teko/ds-icon';
import { MegaMenu, TOPBAR_HEIGHT, TekoLink } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { MegaMenuItemInterface } from '@teko/types/pb';
import { requestIdleCallback } from '@teko/utils';
import { RegionPickerSuggestionPopover } from 'components/RegionPickerSuggestionPopover';
import {
  LogoHeader,
  PopupRegionPicker,
  SearchBar,
  UserProfileHeader,
} from 'components/desktop/organisms';
import { NavigationButton } from 'components/desktop/organisms/style';
import { useFeatureFlags, useLocation, useTenantConfig } from 'contexts';
import { getNavItems, isServerless } from 'helpers';
import { useLiteMode, useLoyaltyMemberInfo, useRegionPicker } from 'hooks';
import { t } from 'i18n';
import { CartOverview } from 'modules/cart';
import { NotificationOverview } from 'modules/notification';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useMemo, useState } from 'react';

interface WithLiteProps {
  lite: boolean;
  verticalNavItem: boolean;
}
const StyledNavigationBar = styled.div<WithLiteProps>(
  ({ lite, verticalNavItem }) => `
  @media print {
    display: none;
  }
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1040;
  box-shadow: 0 0.5rem 1rem 0 rgba(0, 0, 0, 0.1);
  @media screen and (max-width: 767px) {
    height: auto !important;
  }

  ${
    lite && verticalNavItem
      ? `
      min-width: 68px;
      .title {
        visibility: hidden;
        height: 0;
      }
      .button {
        gap: 0;
      }
    `
      : ``
  }
`,
);
export const NavItem = styled.div(
  ({ theme }: ThemeProps) => `
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  &:hover {
    div {
      color: ${theme?.colors.primary500} !important;
    }
    path {
      fill: ${theme?.colors.primary500} !important;
    }
  }
`,
);
const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 550;
  background: rgba(0, 0, 0, 0.5);
  -webkit-transition-delay: 0.1s;
  transition-delay: 0.1s;
`;

const StyledContainer = styled(Container)`
  min-width: fit-content;
`;

const Keywords = styled.p`
  min-width: 0;
  margin-top: 0.5rem;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Keyword = styled(TekoLink)`
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: ${({ theme }) => theme.colors.neutral};

  &:hover {
    color: ${({ theme }) => theme.colors.primary500};
  }
`;

interface DisplayConfigInterface {
  order?: boolean;
  promotion?: boolean;
  login?: boolean;
  cart?: boolean;
  additional?: React.ReactNode;
}
interface NavigationBarProps {
  showShippingFeeMiniCart?: boolean;
  menuItems?: MegaMenuItemInterface[];
  liteLogo?: boolean;
  displayConfig?: DisplayConfigInterface;
}

export const isVerticalNavItem = (tenant: string) => {
  switch (tenant) {
    case 'ipp':
      return true;
    default:
      return false;
  }
};
export const defaultNavItemDisplayConfig: DisplayConfigInterface = {
  order: false,
  promotion: false,
  login: true,
  cart: true,
  additional: undefined,
};

export const NavigationBar: React.FunctionComponent<NavigationBarProps> = ({
  showShippingFeeMiniCart = true,
  menuItems,
  liteLogo,
  displayConfig,
}: NavigationBarProps) => {
  displayConfig = { ...defaultNavItemDisplayConfig, ...displayConfig };
  const {
    partner,
    tenant,
    megaMenuType = 'vertical',
    alignMegaMenu = 'left',
    notificationAppId,
    popularKeywords,
    epicOmni948,
    epicOmni1538,
    notFetchLoyaltyMemberInfoGlobally,
    displayNewLayout,
  } = useTenantConfig();
  const { flags } = useFeatureFlags();
  const { user, authService } = useAuth();
  const { loyaltyMemberInfo } = useLoyaltyMemberInfo(
    !notFetchLoyaltyMemberInfoGlobally,
  );
  const profileLinks = useMemo(() => {
    return getNavItems({
      tenant,
      logged: !!user?.userId,
      showLoyaltyItem:
        (notFetchLoyaltyMemberInfoGlobally
          ? !!user?.loyaltyMemberId
          : !!loyaltyMemberInfo) || !!epicOmni1538,
      showNotificationItem: !!notificationAppId,
      epicOmni948,
      epicOmni1546: true,
      displayNewLayout: displayNewLayout,
    });
  }, [tenant, user, loyaltyMemberInfo, epicOmni948]);

  const regionPickerProps = useRegionPicker();
  const region = regionPickerProps?.activeLocation;

  const {
    setShowed: showLocationPopup,
    selectedLocation,
    showRegionPickerSuggestionPopover,
    onCloseRegionPickerSuggestionPopover,
    enabled: locationPickerEnabled,
  } = useLocation();
  const onLogin = () => authService.login(window.location.href);
  const router = useRouter();
  const [navOffsetTop, setNavOffsetTop] = useState(0);
  const { isLite, dropdownOpen, toggle } = useLiteMode(
    !!liteLogo,
    navOffsetTop,
  );
  const LoginIcon = (
    <UserProfileHeader
      vertical={isVerticalNavItem(tenant)}
      customProfile={
        !!partner ? (
          <CustomLink href="/account/orders">
            <NavigationButton className="button">
              <ChecklistOutlined size={36} color="textSecondary" />
              <div>{t('Quản lý đơn hàng')}</div>
            </NavigationButton>
          </CustomLink>
        ) : undefined
      }
      profileLinks={profileLinks}
    />
  );
  const PromotionIcon = (
    <CustomLink href="/khuyen-mai" target="_blank">
      <NavigationButton
        className="button"
        style={
          isVerticalNavItem(tenant)
            ? { flexDirection: 'column' }
            : { flexDirection: 'row' }
        }
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'promotionLandingIcon',
        })}
      >
        <PromoOutlined size={36} color="textSecondary" />
        <span className="title">{t('Khuyến mãi')}</span>
      </NavigationButton>
    </CustomLink>
  );
  const OrderIcon = (
    <NavigationButton
      className="button"
      onClick={(): void => {
        if (!user?.userId) {
          onLogin();
        } else {
          router.push('/account/orders');
        }
      }}
      style={
        isVerticalNavItem(tenant)
          ? { flexDirection: 'column' }
          : { flexDirection: 'row' }
      }
      {...getTrackingData({
        isTrackContent: true,
        regionName: 'headerBar',
        contentName: 'orderIcon',
      })}
    >
      <OrderOutlined size={36} color="textSecondary" />
      <span className="title">{t('Đơn hàng')}</span>
    </NavigationButton>
  );

  const NotificationIcon = (
    <NotificationOverview title={isVerticalNavItem(tenant)} />
  );

  const CartIcon = (
    <CartOverview
      showShippingFeeMiniCart={showShippingFeeMiniCart}
      vertical={isVerticalNavItem(tenant)}
    />
  );

  const RegionPickerIcon = ({ locationName }: { locationName?: string }) => {
    const showRegionPickerModal = () => {
      if (flags?.epic_ecom_284) {
        showLocationPopup?.(true);
      } else {
        regionPickerProps.onOpenPopup();
      }
    };

    // Don't show this icon on the server side
    if (!isServerless()) {
      return null;
    }

    return (
      <RegionPickerSuggestionPopover
        active={!!showRegionPickerSuggestionPopover}
        onClose={onCloseRegionPickerSuggestionPopover ?? (() => {})}
      >
        <NavItem
          onClick={showRegionPickerModal}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'location',
          })}
          className="att-home-page-delivery-area"
        >
          <LocationOutlined size={36} color="textSecondary" />
          <Shape>
            <Caption color="textSecondary">{t('Xem sản phẩm tại')}</Caption>
            <Body level={2} weight="medium" color="textSecondary" line={1}>
              {locationName}
            </Body>
          </Shape>
          <DownOutlined size={24} color="textSecondary" />
        </NavItem>
      </RegionPickerSuggestionPopover>
    );
  };

  const getNavMenuByDisplayConfig = () => {
    const { order, promotion, login, cart, additional } = displayConfig || {};
    const navMenu = [];
    if (promotion) navMenu.push(PromotionIcon);
    if (order) navMenu.push(OrderIcon);
    if (login) navMenu.push(LoginIcon);
    if (notificationAppId) navMenu.push(NotificationIcon);
    if (cart) navMenu.push(CartIcon);
    if (locationPickerEnabled) {
      navMenu.push(
        <RegionPickerIcon locationName={selectedLocation?.locationName} />,
      );
    } else if (
      regionPickerProps?.isShowLocationIcon &&
      region &&
      regionPickerProps?.terminals?.length > 1
    ) {
      navMenu.push(<RegionPickerIcon locationName={region?.label} />);
    }
    if (additional) navMenu.push(additional);

    return navMenu;
  };
  const [navItems, setNavItems] = useState(getNavMenuByDisplayConfig());

  useEffect(() => {
    setNavItems(getNavMenuByDisplayConfig());
  }, [user?.userId, selectedLocation, loyaltyMemberInfo]);

  return (
    <>
      {dropdownOpen && <Overlay />}
      <StyledNavigationBar
        lite={isLite}
        verticalNavItem={isVerticalNavItem(tenant)}
        id="mainNavigationBar"
        ref={(navRef: HTMLDivElement) => {
          requestIdleCallback((deadline) => {
            if (navRef && deadline.timeRemaining() > 0) {
              const { scrollTop } = document.documentElement;
              setNavOffsetTop(scrollTop % TOPBAR_HEIGHT);
            }
          });
        }}
      >
        <Row justify="center" paddingLeft="s8" paddingRight="s8">
          <Row
            gutter={16}
            align="middle"
            style={{
              ...(megaMenuType === 'vertical' &&
              isVerticalNavItem(tenant) &&
              isLite
                ? { height: '72px' }
                : !popularKeywords?.length
                ? { height: '88px' }
                : { height: '112px' }),
              minWidth: 0,
              width: '75rem',
            }}
            wrap={false}
          >
            <Col flex="0 0 auto">
              <LogoHeader
                isLite={
                  megaMenuType === 'vertical' &&
                  (typeof window !== 'undefined' ? isLite : !!liteLogo)
                }
                dropdownOpen={dropdownOpen}
                toggle={toggle}
                // Only show mega menu dropdown on type vertical
                menuItems={megaMenuType === 'vertical' ? menuItems : []}
              />
            </Col>
            <Col
              flex="1 1 auto"
              style={{
                paddingTop: popularKeywords?.length ? '1.75rem' : '0',
                minWidth: 0,
              }}
            >
              <SearchBar />
              {!!popularKeywords?.length && (
                <Keywords>
                  {popularKeywords.map(({ keyword, link }, i) => (
                    <Fragment key={keyword}>
                      {i > 0 && <>&nbsp;&nbsp;&nbsp;</>}
                      <Keyword href={link}>{keyword}</Keyword>
                    </Fragment>
                  ))}
                </Keywords>
              )}
            </Col>
            {navItems.map((item, index) => (
              <Col flex="0 0 auto" key={index}>
                {item}
              </Col>
            ))}
          </Row>
        </Row>
        {megaMenuType === 'horizontal' && (
          <>
            <Divider gap={0} color="divider" />
            <StyledContainer>
              <Row>
                <Col span={12}>
                  <MegaMenu
                    type="horizontal"
                    align={alignMegaMenu}
                    menuItems={menuItems ?? []}
                  />
                </Col>
              </Row>
            </StyledContainer>
          </>
        )}
      </StyledNavigationBar>
      {!flags?.epic_ecom_284 && <PopupRegionPicker {...regionPickerProps} />}
    </>
  );
};
